import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["popup", "searchInput", "results", "selectedContainer", "selectedList", "submitButton"]
  static values = { conversationId: Number, existingParticipantIds: Array }
  
  selectedMembers = []
  
  connect() {
    // Close popup when clicking outside
    this.boundClickOutside = this.clickOutside.bind(this)
    document.addEventListener("click", this.boundClickOutside)
  }
  
  disconnect() {
    document.removeEventListener("click", this.boundClickOutside)
  }
  
  toggle(event) {
    event.stopPropagation()
    this.popupTarget.classList.toggle("d-none")
    
    if (!this.popupTarget.classList.contains("d-none")) {
      this.searchInputTarget.focus()
    }
  }
  
  close() {
    this.popupTarget.classList.add("d-none")
    this.clearSearch()
    this.selectedMembers = []
    this.renderSelectedMembers()
    this.updateSubmitButton()
  }
  
  clickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }
  
  search() {
    const query = this.searchInputTarget.value.trim()
    
    if (query.length < 2) {
      this.resultsTarget.classList.add("d-none")
      this.resultsTarget.innerHTML = ""
      return
    }
    
    // Fetch search results (same endpoint as new conversation)
    fetch(`/conversations/search_recipients?query=${encodeURIComponent(query)}`)
      .then(response => response.json())
      .then(data => {
        this.renderResults(data.organizations)
      })
      .catch(error => {
        console.error("Error searching members:", error)
      })
  }
  
  renderResults(organizations) {
    if (!organizations || organizations.length === 0) {
      this.resultsTarget.classList.add("d-none")
      this.resultsTarget.innerHTML = ""
      return
    }
    
    let html = ""
    let visibleOrgCount = 0
    
    organizations.forEach((org) => {
      // Filter users first - exclude existing participants and already selected members
      const visibleUsers = org.users.filter(user => {
        if (this.existingParticipantIdsValue.includes(user.id)) return false
        if (this.selectedMembers.find(m => m.id === user.id)) return false
        return true
      })
      
      // Skip this org entirely if no visible users
      if (visibleUsers.length === 0 && !org.has_more) return
      
      // Add organization header
      if (org.name) {
        if (visibleOrgCount > 0) {
          html += `<div class="recipient-org-separator"></div>`
        }
        html += `<div class="recipient-org-header">${this.escapeHtml(org.name)}</div>`
        visibleOrgCount++
      }
      
      // Add users
      visibleUsers.forEach(user => {
        const initials = this.getInitials(user.name)
        const avatarColor = this.getAvatarColor(user.id, user.name)
        const avatarHtml = user.avatar_url 
          ? `<img src="${user.avatar_url}" alt="${this.escapeHtml(user.name)}" class="avatar-image">`
          : initials
        
        html += `
          <div class="add-member-result-item" data-action="click->add-member#selectMember" data-user-id="${user.id}" data-user-name="${this.escapeHtml(user.name)}">
            <div class="result-avatar" style="background-color: ${avatarColor}">${avatarHtml}</div>
            <div class="result-info">
              <span class="result-name">${this.escapeHtml(user.name)}</span>
            </div>
          </div>
        `
      })
      
      // Add "Show all X users" link if there are more
      if (org.has_more && org.id) {
        html += `
          <div class="recipient-show-more" data-action="click->add-member#showAllOrgUsers" data-org-id="${org.id}">
            <div class="show-more-spacer"></div>
            <span>Show all ${org.total_users} users</span>
          </div>
        `
      }
    })
    
    if (html) {
      this.resultsTarget.innerHTML = html
      this.resultsTarget.classList.remove("d-none")
    } else {
      this.resultsTarget.classList.add("d-none")
      this.resultsTarget.innerHTML = ""
    }
  }
  
  showAllOrgUsers(event) {
    event.stopPropagation()
    const orgId = event.currentTarget.dataset.orgId
    
    fetch(`/conversations/search_recipients?org_id=${orgId}&show_all=true`)
      .then(response => response.json())
      .then(data => {
        this.renderResults(data.organizations)
      })
      .catch(error => {
        console.error("Error fetching org users:", error)
      })
  }
  
  selectMember(event) {
    event.stopPropagation() // Prevent popup from closing
    
    const userId = parseInt(event.currentTarget.dataset.userId)
    const userName = event.currentTarget.dataset.userName
    
    // Add to selected members
    if (!this.selectedMembers.find(m => m.id === userId)) {
      this.selectedMembers.push({ id: userId, name: userName })
      this.renderSelectedMembers()
    }
    
    // Remove the selected item from the results list
    event.currentTarget.remove()
    
    // Hide results if empty
    if (this.resultsTarget.querySelectorAll('.add-member-result-item').length === 0) {
      this.resultsTarget.classList.add("d-none")
    }
    
    this.updateSubmitButton()
    
    // Refocus the search input
    this.searchInputTarget.focus()
  }
  
  removeMember(event) {
    event.stopPropagation() // Prevent popup from closing
    
    const userId = parseInt(event.currentTarget.dataset.userId)
    this.selectedMembers = this.selectedMembers.filter(m => m.id !== userId)
    this.renderSelectedMembers()
    this.updateSubmitButton()
  }
  
  renderSelectedMembers() {
    if (this.selectedMembers.length === 0) {
      this.selectedContainerTarget.classList.add("d-none")
      this.selectedListTarget.innerHTML = ""
      return
    }
    
    let html = ""
    this.selectedMembers.forEach(member => {
      html += `
        <span class="selected-member-tag">
          ${this.escapeHtml(member.name)}
          <button type="button" class="btn-remove-member" data-action="click->add-member#removeMember" data-user-id="${member.id}">
            <i class="fa-solid fa-times"></i>
          </button>
        </span>
      `
    })
    
    this.selectedListTarget.innerHTML = html
    this.selectedContainerTarget.classList.remove("d-none")
  }
  
  clearSearch() {
    this.searchInputTarget.value = ""
    this.resultsTarget.classList.add("d-none")
    this.resultsTarget.innerHTML = ""
  }
  
  updateSubmitButton() {
    this.submitButtonTarget.disabled = this.selectedMembers.length === 0
  }
  
  submit() {
    if (this.selectedMembers.length === 0) return
    
    // Disable button to prevent double submit
    this.submitButtonTarget.disabled = true
    this.submitButtonTarget.textContent = "Adding..."
    
    const memberIds = this.selectedMembers.map(m => m.id)
    
    fetch(`/conversations/${this.conversationIdValue}/add_participants`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify({ user_ids: memberIds })
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          // Update the participants list in the header
          const participantsContainer = this.element.closest('.d-flex')
          const participantsSpan = participantsContainer?.querySelector('.text-truncate')
          if (participantsSpan && data.participants_html) {
            participantsSpan.textContent = data.participants_text
          }
          
          // Close popup and reset
          this.close()
          
          // Reload the conversation to show updated participants
          window.location.reload()
        } else {
          alert(data.error || 'Failed to add members')
          this.submitButtonTarget.disabled = false
          this.submitButtonTarget.textContent = "Add"
        }
      })
      .catch(error => {
        console.error("Error adding members:", error)
        alert('Failed to add members. Please try again.')
        this.submitButtonTarget.disabled = false
        this.submitButtonTarget.textContent = "Add"
      })
  }
  
  getInitials(name) {
    if (!name) return "?"
    // Remove "Dr." prefix if present
    let cleanName = name.replace(/^Dr\.?\s*/i, '').trim()
    
    const parts = cleanName.split(" ")
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    }
    return cleanName.substring(0, 2).toUpperCase()
  }
  
  // Get a consistent color based on user ID or name
  getAvatarColor(userId, name) {
    const colors = [
      '#4F46E5', '#7C3AED', '#DB2777', '#DC2626', '#EA580C',
      '#CA8A04', '#16A34A', '#0D9488', '#0284C7', '#6366F1'
    ]
    // Use user ID if available, otherwise hash the name
    const index = userId ? userId % colors.length : name.length % colors.length
    return colors[index]
  }
  
  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
